import { useEffect, useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import auth from "../../fire";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { ClipLoader } from "react-spinners";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

interface ResetPasswordFormProps {
  oobCode: string | null;
}

// Password-reset UI for a Firebase `resetPassword` oobCode. Rendered both by the
// /auth/action dispatcher and the legacy /reset-password route.
const ResetPasswordForm = ({ oobCode }: ResetPasswordFormProps) => {
  const [emailForCode, setEmailForCode] = useState("");
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!oobCode) {
        setVerifyError("Invalid or missing reset link.");
        setVerifying(false);
        return;
      }
      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmailForCode(email);
      } catch (e) {
        setVerifyError("This reset link is invalid or has expired.");
      } finally {
        setVerifying(false);
      }
    };
    run();
  }, [oobCode]);

  const validatePassword = (pwd: string): string => {
    if (!pwd || pwd.length < 7)
      return "Password must be at least 7 characters.";
    if (!/(?=.*[0-9])/.test(pwd))
      return "Password must contain at least 1 number.";
    return "";
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");

    // Guard narrows oobCode to string; in practice the form only renders once
    // the code has already verified, so this never triggers.
    if (!oobCode) {
      setErrorMessage("Invalid or missing reset link.");
      return;
    }

    const pwdErr = validatePassword(password);
    if (pwdErr) {
      setErrorMessage(pwdErr);
      return;
    }
    if (password !== confirm) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (e) {
      setErrorMessage("Could not reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="flex items-center justify-center py-24">
        <ClipLoader
          color="rgb(79 70 229)"
          speedMultiplier={0.6}
          size={"2rem"}
        />
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="flex flex-col py-12 px-4">
        <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900">
          Reset link issue
        </h2>
        <p className="text-center text-sm text-gray-900 mt-2">{verifyError}</p>
        <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
          <Link
            to="/forgot-password"
            className="text-indigo-600 font-medium text-sm"
          >
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col py-12 px-4">
        <div className="w-full">
          <CheckCircleIcon
            className="h-auto w-12 mx-auto text-indigo-600"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Password updated
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            Your password has been reset for {emailForCode}.
          </p>
        </div>
        <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
          <Link to="/login" className="text-sm text-indigo-600 font-medium">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex py-12 px-4">
      <div className="w-full">
        <div className="flex flex-col items-center mb-8">
          <img
            className="mx-auto w-16 h-auto"
            src="/superset-icon.png"
            alt="Superset"
          />
          <h1 className="mb-1 mt-1 font-bold leading-7 text-indigo-600 text-4xl tracking-tight">
            Superset
          </h1>
          <span className="font-normal text-med tracking-[1.4px] text-indigo-600">
            Workout Tracker
          </span>
        </div>
        <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900">
          Set a new password
        </h2>
        <p className="text-center text-sm text-gray-900 mt-2">
          Resetting password for {emailForCode}
        </p>

        <form className="mt-8" onSubmit={handleSubmit}>
          <div className="space-y-3">
            <div className="relative">
              <label htmlFor="newPassword" className="sr-only">
                New password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type={showPassword ? "text" : "password"}
                className="relative block w-full appearance-none rounded-md shadow-sm border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 z-10 flex items-center pr-3"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeSlashIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                ) : (
                  <EyeIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
            <div className="relative">
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                className="relative block w-full appearance-none rounded-md shadow-sm border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 z-10 flex items-center pr-3"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeSlashIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                ) : (
                  <EyeIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
          </div>

          <div className="mt-1 min-h-[1.25rem]">
            {errorMessage && (
              <div className="text-sm text-red-600">{errorMessage}</div>
            )}
          </div>

          <div>
            <button
              type="submit"
              className="mt-4 group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
              disabled={submitting}
            >
              {!submitting && `Update password`}
              {submitting && (
                <ClipLoader
                  color="rgb(white)"
                  speedMultiplier={0.6}
                  size={"1.25rem"}
                />
              )}
            </button>
          </div>
        </form>

        <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
          <Link to="/login" className="text-sm text-indigo-600 font-medium">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
